import { useEffect, useMemo, useState } from "react";
import { useEventBus, getRobotEvent, ROBOT_EVENTS } from "shared/services/eventBus";

/**
 * Who is connected to a robot, across all of its peers.
 *
 * Source of truth is conn-manager: every peer document (a "robot client" in conn-manager
 * terms) carries `connecting_user_clients` ({ "<username>#<n>": { role, state } }, reported
 * by the robot's ketirtc ConnectionManager). The REST snapshot (`peerInfos`) seeds the
 * state; afterwards conn-manager broadcasts every change to all users as
 *   - robot_event / cnn_peers_updated  → robot:<peerId>:peers:updated  { connecting_user_clients }
 *   - p2p_event                        → robot:<peerId>:p2p:event      { updated_data: { connecting_user_clients } }
 * and a robot_event / disconnected (robot:<peerId>:disconnected) means the peer dropped
 * every user.
 */

const USER_STATE_RANK = { connected: 3, connecting: 2, disconnected: 1, closed: 0 };

/** `{ id: { role, state } }` → the same map with only plain { role, state } entries. */
export const normalizeUserClients = (userClients) => {
  if (userClients == null || typeof userClients !== "object" || Array.isArray(userClients)) {
    return {};
  }
  const out = {};
  Object.entries(userClients).forEach(([userClientId, info]) => {
    if (!userClientId) return;
    out[userClientId] = {
      role: info?.role ?? null,
      state: info?.state ?? null,
    };
  });
  return out;
};

/** "alice#2" → "alice" (the websocket client id is `<username>#<n>`, see conn-manager). */
export const userClientIdToUsername = (userClientId) =>
  typeof userClientId === "string" ? userClientId.split("#")[0] : "";

/**
 * Merge the per-peer maps into one list, one entry per user client, so that a user
 * connected to several peers of the robot (camera + control) shows up once.
 *
 * @param {Object} usersByPeer - { [peerId]: { [userClientId]: { role, state } } }
 * @returns {Array<{ user_client_id, username, role, state, peers: Array<{ peer_id, role, state }> }>}
 *   sorted controllers first, then by user client id. `role` is "controller" when the user
 *   controls any peer; `state` is the best state over the peers.
 */
export const aggregateConnectedUsers = (usersByPeer) => {
  const byUser = {};
  Object.entries(usersByPeer || {}).forEach(([peerId, users]) => {
    Object.entries(users || {}).forEach(([userClientId, info]) => {
      const entry =
        byUser[userClientId] ||
        (byUser[userClientId] = {
          user_client_id: userClientId,
          username: userClientIdToUsername(userClientId),
          role: null,
          state: null,
          peers: [],
        });
      entry.peers.push({ peer_id: peerId, role: info?.role ?? null, state: info?.state ?? null });
      if (info?.role === "controller" || entry.role == null) {
        entry.role = info?.role ?? entry.role;
      }
      const rank = USER_STATE_RANK[info?.state] ?? -1;
      if (rank > (USER_STATE_RANK[entry.state] ?? -1)) entry.state = info?.state ?? null;
    });
  });
  return Object.values(byUser).sort((a, b) => {
    const ac = a.role === "controller" ? 0 : 1;
    const bc = b.role === "controller" ? 0 : 1;
    return ac - bc || a.user_client_id.localeCompare(b.user_client_id);
  });
};

/**
 * @param {Array<Object>} peerInfos - the robot's peers as registered with conn-manager
 *   (`/api/user/robots/<robotId>`), each with `id` and `connecting_user_clients`
 * @returns {{ usersByPeer: Object, users: Array }} see aggregateConnectedUsers
 */
export const useRobotConnectedUsers = (peerInfos) => {
  const eventBus = useEventBus();
  const [usersByPeer, setUsersByPeer] = useState({});

  const peerIds = useMemo(
    () => (peerInfos || []).map((peer) => peer?.id).filter(Boolean),
    [peerInfos]
  );

  // Snapshot from the REST response (conn-manager keeps it updated in its DB)
  useEffect(() => {
    const next = {};
    (peerInfos || []).forEach((peer) => {
      if (peer?.id) next[peer.id] = normalizeUserClients(peer.connecting_user_clients);
    });
    setUsersByPeer(next);
  }, [peerInfos]);

  // Live updates pushed by conn-manager over the user websocket
  useEffect(() => {
    if (peerIds.length === 0) return undefined;
    const subs = [];
    peerIds.forEach((peerId) => {
      const setPeerUsers = (users) =>
        setUsersByPeer((prev) => ({ ...prev, [peerId]: normalizeUserClients(users) }));

      subs.push(
        eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.PEERS_UPDATED), (evt) =>
          setPeerUsers(evt?.connecting_user_clients)
        )
      );
      subs.push(
        eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.P2P_EVENT), (evt) => {
          const users = evt?.updated_data?.connecting_user_clients;
          if (users) setPeerUsers(users);
        })
      );
      subs.push(
        eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.DISCONNECTED), () => setPeerUsers({}))
      );
    });
    return () => subs.forEach((unsub) => { if (typeof unsub === "function") unsub(); });
  }, [eventBus, peerIds]);

  const users = useMemo(() => aggregateConnectedUsers(usersByPeer), [usersByPeer]);

  return { usersByPeer, users };
};

export default useRobotConnectedUsers;
